/*
  cb-home.js — shared machinery for the cb-home-* homepage sections.
  Self-guarded: safe to include from multiple sections.

  1) ADD TO CART — any `form[data-cb-atc]`:
     - posts FormData to theme.routes.cart_add_url with the Section Rendering API
       params the Shapes cart drawer expects, then dispatches
       `shapes:modalcart:afteradditem` so the existing drawer/count/upsell
       machinery takes over (same contract as assets/product-quick-buy.js).
     - button: `.cb-atc` with data-add-label / data-soldout-label / data-adding-label.
     - inline errors go to `[data-cb-atc-error]`; successes/errors are announced
       via a polite live region.

  2) SHOPPABLE VIDEO DIALOG — trigger: `[data-cb-video-open="<dialog id>"]`
     inside a card carrying `[data-cb-card]` plus data attributes:
       data-cb-video-src      mp4/m3u8 URL (a `video.sources` transcode, never originalSource)
       data-cb-video-poster   poster image URL
       data-cb-title          product title
       data-cb-price          money-formatted current price
       data-cb-variant-id     variant to add
       data-cb-available      "true" | "false"
       data-cb-url            product details URL
       data-cb-description    optional one-liner
     Dialog markup contract (one <dialog class="cb-video-dialog" id="..."> per section):
       <video preload="none" controls playsinline></video>
       [data-cb-d-title] [data-cb-d-price] [data-cb-d-desc] [data-cb-d-link]
       form[data-cb-atc] with <input name="id">, button.cb-atc
       button[data-cb-dialog-close]
     Native <dialog>.showModal() supplies Esc-to-close + focus containment;
     this script pauses the video and returns focus to the opener on close.
*/
(function () {
  'use strict';
  if (window.__cbHome) return;
  window.__cbHome = true;

  function liveRegion() {
    var el = document.getElementById('cb-live-region');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cb-live-region';
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('role', 'status');
      el.style.cssText =
        'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;';
      document.body.appendChild(el);
    }
    return el;
  }
  function announce(msg) {
    var el = liveRegion();
    el.textContent = '';
    window.setTimeout(function () { el.textContent = msg; }, 50);
  }

  /* ---------------- Add to cart ---------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target && e.target.closest ? e.target.closest('form[data-cb-atc]') : null;
    if (!form) return;
    e.preventDefault();

    var btn = form.querySelector('.cb-atc');
    var err = form.querySelector('[data-cb-atc-error]');
    if (btn && btn.disabled) return;

    var addLabel = btn ? (btn.dataset.addLabel || btn.textContent) : '';
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = btn.dataset.addingLabel || 'ADDING...';
    }
    if (err) { err.hidden = true; err.textContent = ''; }

    var settings = (window.theme && window.theme.settings) || {};
    var routes = (window.theme && window.theme.routes) || {};
    var modalCart = settings.cart_type === 'modal';

    var fd = new FormData(form);
    if (modalCart) {
      fd.append('sections', 'cart-items,cart-footer,cart-item-count');
      fd.append('sections_url', window.location.pathname);
    }

    function restore() {
      if (!btn) return;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.textContent = addLabel;
    }

    fetch(routes.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: fd,
    })
      .then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; });
      })
      .then(function (r) {
        restore();
        if (!r.ok || (r.data && r.data.status)) {
          var msg = (r.data && (r.data.description || r.data.message)) || 'Could not add to cart. Please try again.';
          if (err) { err.textContent = msg; err.hidden = false; }
          announce(msg);
          return;
        }
        if (modalCart) {
          document.body.dispatchEvent(
            new CustomEvent('shapes:modalcart:afteradditem', {
              bubbles: true,
              detail: { response: r.data },
            })
          );
        } else {
          window.location.href = routes.cart_url || '/cart';
          return;
        }
        announce('Added to cart');
      })
      .catch(function () {
        restore();
        var msg = 'Network error. Please try again.';
        if (err) { err.textContent = msg; err.hidden = false; }
        announce(msg);
      });
  });

  /* ---------------- Shoppable video dialog ---------------- */
  var lastOpener = null;

  function setText(root, sel, text) {
    var el = root.querySelector(sel);
    if (el) el.textContent = text || '';
  }

  function openDialog(trigger) {
    var card = trigger.closest('[data-cb-card]');
    var dialogId = trigger.getAttribute('data-cb-video-open');
    var dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!card || !dialog || typeof dialog.showModal !== 'function') {
      // No dialog support: fall back to the product page.
      if (card && card.dataset.cbUrl) window.location.href = card.dataset.cbUrl;
      return;
    }
    // The poster div is also a trigger, but it is not focusable. Restore focus
    // to the card's real play button so closing the dialog lands somewhere sane.
    lastOpener = trigger.matches && trigger.matches('button, a, [tabindex]')
      ? trigger
      : (card.querySelector('button[data-cb-video-open]') || trigger);

    setText(dialog, '[data-cb-d-title]', card.dataset.cbTitle);
    setText(dialog, '[data-cb-d-price]', card.dataset.cbPrice);
    setText(dialog, '[data-cb-d-desc]', card.dataset.cbDescription || '');

    var link = dialog.querySelector('[data-cb-d-link]');
    if (link) {
      if (card.dataset.cbUrl) { link.href = card.dataset.cbUrl; link.hidden = false; }
      else { link.hidden = true; }
    }

    var idInput = dialog.querySelector('form[data-cb-atc] input[name="id"]');
    if (idInput) idInput.value = card.dataset.cbVariantId || '';
    var atcBtn = dialog.querySelector('form[data-cb-atc] .cb-atc');
    if (atcBtn) {
      var available = card.dataset.cbAvailable !== 'false' && !!card.dataset.cbVariantId;
      atcBtn.disabled = !available;
      atcBtn.textContent = available
        ? atcBtn.dataset.addLabel || 'ADD TO CART'
        : atcBtn.dataset.soldoutLabel || 'SOLD OUT';
    }
    var atcErr = dialog.querySelector('[data-cb-atc-error]');
    if (atcErr) { atcErr.hidden = true; atcErr.textContent = ''; }

    var video = dialog.querySelector('video');
    var src = card.dataset.cbVideoSrc || '';
    if (video) {
      if (src) {
        if (video.getAttribute('src') !== src) video.setAttribute('src', src);
        if (card.dataset.cbVideoPoster) video.setAttribute('poster', card.dataset.cbVideoPoster);
        video.hidden = false;
      } else {
        video.removeAttribute('src');
        video.hidden = true;
      }
      // Closed captions: swap in the card's VTT track (cross-origin OK, the
      // video carries crossorigin="anonymous" and the Shopify CDN sends CORS).
      var oldTrack = video.querySelector('track');
      if (oldTrack) oldTrack.remove();
      var capSrc = card.dataset.cbCaptions || '';
      if (capSrc && src) {
        var tr = document.createElement('track');
        tr.kind = 'captions';
        tr.label = 'English';
        tr.srclang = 'en';
        tr.src = capSrc;
        tr.default = true;
        video.appendChild(tr);
      }
    }

    dialog.addEventListener('close', function onClose() {
      var v = dialog.querySelector('video');
      if (v) v.pause();
      if (lastOpener) {
        try { lastOpener.focus(); } catch (err) { /* opener may be gone */ }
        lastOpener = null;
      }
    }, { once: true });

    dialog.showModal();

    if (video && src) {
      var p = video.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked: user taps native controls */ });
    }
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target && e.target.closest ? e.target.closest('[data-cb-video-open]') : null;
    if (trigger) {
      e.preventDefault();
      openDialog(trigger);
      return;
    }
    var closeBtn = e.target && e.target.closest ? e.target.closest('[data-cb-dialog-close]') : null;
    if (closeBtn) {
      var d = closeBtn.closest('dialog');
      if (d) d.close();
      return;
    }
    // Backdrop click: the dialog element itself is the target only when the
    // click lands outside the dialog's content box.
    if (
      e.target instanceof HTMLElement &&
      e.target.tagName === 'DIALOG' &&
      e.target.classList.contains('cb-video-dialog')
    ) {
      e.target.close();
    }
  });
})();
